import React, { FC, useState, useEffect } from 'react';

import { Box, Button, Card, Chip, Divider, Grid, MenuItem, Select, Typography } from '@mui/material';

import SaveIcon from '@mui/icons-material/Save';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { minimumLoading } from '../../../utils/minimumLoading';

import { BackflowJobGroup } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

interface WaterAuthoritySelectionDropdownProps {
  backflowJobGroup: BackflowJobGroup;
  fetchBackflowJobGroup: () => void;
}

const WaterAuthoritySelectionDropdown: FC<WaterAuthoritySelectionDropdownProps> = (props) => {
  const { backflowJobGroup, fetchBackflowJobGroup } = props;

  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [optionsFetched, setOptionsFetched] = useState<boolean>(false);
  const [selectedWaterAuthority, setSelectedWaterAuthority] = useState<any>('Unknown');
  const [waterAuthorityOptionList, setWaterAuthorityOptionList] = useState<any>([]);

  const fetchWaterAuthorityOptionList = async () => {
    try {
      const response: any = await axios.get('/pick_list_options', {
        params: { model: 'WaterAuthority' },
      });
      const fetchedOptions = response.data.result;
      fetchedOptions.unshift({ value: 'Unknown', label: 'Unknown' });
      setWaterAuthorityOptionList(fetchedOptions);
      setSelectedWaterAuthority(
        fetchedOptions.find((option: any) => option.value === backflowJobGroup?.water_authority_name)?.value ||
          'Unknown',
      );
      setOptionsFetched(true);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong fetching the Water Authority options');
    }
  };

  const updateWaterAuthority = async () => {
    try {
      await axios.patch(`/sales_estimates/customers/${backflowJobGroup?.customer_id}`, {
        customer: { water_authority_id: selectedWaterAuthority },
      });
      setErrorMessage('');
      fetchBackflowJobGroup();
      setDisplayDropdown(false);
    } catch (error) {
      setErrorMessage('Something went wrong updating the Water Authority');
    }
  };

  const handleUpdateClicked = () => {
    minimumLoading(updateWaterAuthority, setLoading);
  };

  useEffect(() => {
    if (!optionsFetched) {
      fetchWaterAuthorityOptionList();
    }
  }, []);

  useEffect(() => {
    if (backflowJobGroup?.water_authority_name === 'Unknown') {
      setDisplayDropdown(true);
    }
  }, [backflowJobGroup?.water_authority_name]);

  const disableUpdateButton =
    selectedWaterAuthority === 'Unknown' || selectedWaterAuthority === backflowJobGroup?.water_authority_name;

  const openDropdown = () => {
    setDisplayDropdown(true);
    const selectedValue =
      waterAuthorityOptionList.find((option: any) => option.label === backflowJobGroup?.water_authority_name)?.value ||
      'Unknown';
    setSelectedWaterAuthority(selectedValue);
  };

  return (
    <Card elevation={3} style={{ paddingTop: '2%', paddingRight: '4%', paddingBottom: '4%', paddingLeft: '4%' }}>
      <LoadingWrapper isLoading={loading} message="Loading...">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <WaterDropIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="600">
                Water Authority
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
          </Grid>
          {displayDropdown ? (
            <>
              <Grid item xs={12}>
                <Select
                  value={selectedWaterAuthority}
                  onChange={(e) => setSelectedWaterAuthority(e.target.value)}
                  sx={{ minWidth: 250 }}
                >
                  <MenuItem value="">
                    <em>Choose Water Authority...</em>
                  </MenuItem>
                  {waterAuthorityOptionList?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdateClicked}
                  disabled={disableUpdateButton}
                  size="large"
                >
                  Save
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Chip
                  label={backflowJobGroup.water_authority_name || 'Not set'}
                  color={backflowJobGroup.water_authority_name ? 'primary' : 'default'}
                  variant="outlined"
                />
                <Button variant="contained" color="secondary" onClick={openDropdown}>
                  Change
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </LoadingWrapper>
    </Card>
  );
};

export default WaterAuthoritySelectionDropdown;
